import { ChangeDetectionStrategy, Component, ElementRef, input, signal, viewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatLabel } from '@angular/material/input';
import { AbstractControl, FormControl } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { FileWithStatus } from '@interfaces';

@Component({
  selector: 'sky-media-upload',
  imports: [NgClass, MatLabel],
  templateUrl: './media-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaUploadComponent {
  control = input.required<FormControl>();
  label = input<string>();
  mediaAccepted = input.required<string[]>();
  showError = input<boolean, BooleanInput>(true, {
    transform: coerceBooleanProperty,
  });

  input = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  isDragging = signal<boolean>(false);

  get isInvalid() {
    return this.control().invalid && this.control().touched;
  }

  triggerFileDialog() {
    if (!this.input()) {
      return;
    }

    this.input()?.nativeElement?.click();
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.setFiles(Array.from(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    if (this.control()?.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (!event.dataTransfer?.files?.length) {
      return;
    }

    this.setFiles(Array.from(event.dataTransfer.files));
  }

  private setFiles(files: File[]) {
    const currentFilesWithStatus: FileWithStatus[] = this.control().value ?? [];
    const currentFiles: File[] = currentFilesWithStatus.map(f => f.file);
    const allFiles = [...currentFiles, ...files];

    const filesWithStatus: FileWithStatus[] = allFiles.map(file => {
      const errors = this.control().validator?.({ value: [{ file }] } as AbstractControl);
      return {
        file: file,
        valid: !errors,
      };
    });

    this.control().setValue(filesWithStatus);
    this.control().markAsTouched();
  }
}
